import { useCallback, useEffect, useState } from "react";
import * as admin from "../api/admin";
import type { AdminUser } from "../api/types";
import { adminButtons, trialText } from "../lib/dashboard";
import { useSession } from "../state/session";
import { Dialog } from "../ui/Dialog";
import { useDialogs } from "../ui/dialogs";
import { PasswordInput } from "../ui/PasswordInput";

/** admin_dashboard.py: AdminDashboardDialog with the Add User and Reset Password sub-dialogs. */
export function AdminDialog({ onClose }: { onClose: () => void }) {
  const dlg = useDialogs();
  const { user: me } = useSession();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [sub, setSub] = useState<null | "add" | "password">(null);
  const selected = users.find((u) => u.id === selectedId) ?? null;
  const btn = adminButtons(selected, me);

  const load = useCallback(async () => {
    try { setUsers(await admin.listUsers()); }
    catch (e) { await dlg.alert({ title: "Error", text: (e as Error).message }); }
  }, [dlg]);
  useEffect(() => { void load(); }, [load]);

  const attempt = async (fn: () => Promise<unknown>) => {
    try { await fn(); await load(); } catch (e) { await dlg.alert({ title: "Error", text: (e as Error).message }); }
  };
  const toggleRole = async () => {
    if (!selected) return;
    const next = selected.role === "admin" ? "annotator" : "admin";
    if (await dlg.confirm({ title: "Change Role", text: `Set '${selected.username}' as ${next}?` })) await attempt(() => admin.setRole(selected.id, next));
  };
  const resetTrial = async () => {
    if (selected && await dlg.confirm({ title: "Reset Trial", text: `Reset '${selected.username}' trial to a fresh 7 days from now?` })) await attempt(() => admin.resetTrial(selected.id));
  };
  const cell = { padding: "6px 10px" } as const;

  return (
    <Dialog title="Admin Dashboard" onClose={onClose} width={760} testId="admin">
      <div className="muted" style={{ marginBottom: 6 }}>Users</div>
      <div className="list" style={{ padding: 0, maxHeight: 320 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="user-table">
          <thead><tr style={{ textAlign: "left", color: "var(--muted)" }}>
            {["Username", "Email", "Role", "Trial", "Joined"].map((h) => <th key={h} style={{ ...cell, fontWeight: 600 }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} aria-selected={u.id === selectedId} data-testid={`user-${u.username}`} onClick={() => setSelectedId(u.id)}
                style={{ cursor: "pointer", background: u.id === selectedId ? "var(--selection)" : undefined, color: u.id === selectedId ? "#fff" : undefined }}>
                <td style={cell}>{u.username}</td><td style={cell}>{u.email}</td>
                <td style={cell}>{u.role}</td><td style={cell}>{trialText(u)}</td>
                <td style={cell}>{(u.created_at ?? "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="dialog-actions" style={{ justifyContent: "flex-start", flexWrap: "wrap" }}>
        <button className="btn primary" onClick={() => setSub("add")} data-testid="add-user">+ Add User</button>
        <button className="btn primary" disabled={!btn.role} onClick={toggleRole} data-testid="toggle-role">{btn.roleLabel}</button>
        <button className="btn" disabled={!btn.extend} onClick={() => selected && attempt(() => admin.extendTrial(selected.id, 7))} data-testid="extend-trial">Extend Trial +7 days</button>
        <button className="btn" disabled={!btn.resetTrial} onClick={resetTrial} data-testid="reset-trial">Reset Trial</button>
        <button className="btn" disabled={!btn.resetPassword} onClick={() => setSub("password")} data-testid="reset-password">Reset Password</button>
        <span style={{ flex: 1 }} />
        <button className="btn" onClick={load}>Refresh</button>
      </div>
      <div className="dialog-actions"><button className="btn" onClick={onClose}>Close</button></div>
      {sub === "add" && <AddUser onClose={() => setSub(null)} onCreated={() => { setSub(null); void load(); }} />}
      {sub === "password" && selected && <ResetPassword user={selected} onClose={() => setSub(null)} />}
    </Dialog>
  );
}

function AddUser({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const dlg = useDialogs();
  const [f, setF] = useState({ username: "", email: "", password: "", role: "annotator" });
  const submit = async () => {
    if (!f.username.trim() || !f.email.trim() || !f.password) { await dlg.alert({ title: "Missing Fields", text: "All fields are required." }); return; }
    if (f.password.length < 8) { await dlg.alert({ title: "Weak Password", text: "Password must be at least 8 characters." }); return; }
    try { await admin.createUser(f.username.trim(), f.email.trim(), f.password, f.role); onCreated(); }
    catch (e) { await dlg.alert({ title: "Error", text: (e as Error).message }); }
  };
  return (
    <Dialog title="Add User" onClose={onClose} width={380} testId="add-user-dialog">
      <div className="form-row"><label htmlFor="au-u">Username</label><input id="au-u" className="input" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} /></div>
      <div className="form-row"><label htmlFor="au-e">Email</label><input id="au-e" className="input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
      <div className="form-row"><label htmlFor="au-p">Password</label><PasswordInput id="au-p" value={f.password} onChange={(v) => setF({ ...f, password: v })} autoComplete="new-password" /></div>
      <div className="form-row"><label htmlFor="au-r">Role</label>
        <select id="au-r" className="select" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}><option>annotator</option><option>admin</option></select></div>
      <div className="dialog-actions"><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={submit} data-testid="au-create">Create</button></div>
    </Dialog>
  );
}

function ResetPassword({ user, onClose }: { user: AdminUser; onClose: () => void }) {
  const dlg = useDialogs();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const submit = async () => {
    if (!pw) { await dlg.alert({ title: "Missing Password", text: "Enter a new password." }); return; }
    if (pw.length < 8) { await dlg.alert({ title: "Weak Password", text: "Password must be at least 8 characters." }); return; }
    if (pw !== confirm) { await dlg.alert({ title: "Mismatch", text: "Password and confirmation do not match." }); return; }
    try {
      await admin.resetPassword(user.id, pw);
      onClose();
      await dlg.alert({ title: "Password Reset", text: `Password for '${user.username}' has been reset.` });
    } catch (e) { await dlg.alert({ title: "Error", text: (e as Error).message }); }
  };
  return (
    <Dialog title={`Reset Password — ${user.username}`} onClose={onClose} width={380} testId="reset-password-dialog">
      <div className="form-row"><label htmlFor="rpw-n">New password</label><PasswordInput id="rpw-n" value={pw} onChange={setPw} autoComplete="new-password" /></div>
      <div className="form-row"><label htmlFor="rpw-c">Confirm password</label><PasswordInput id="rpw-c" value={confirm} onChange={setConfirm} onEnter={submit} autoComplete="new-password" /></div>
      <div className="dialog-actions"><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={submit} data-testid="rpw-go">Reset Password</button></div>
    </Dialog>
  );
}
